import React, { useState, useEffect } from 'react';
import { Container, Button, Alert, Spinner, Card } from 'react-bootstrap';
import { loadStripe } from '@stripe/stripe-js';
import paymentService from '../services/paymentService';
import featureFlagService, { FEATURES } from '../services/featureFlagService';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLIC_KEY || '');

const PremiumUpgradePage = () => {
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [paymentsEnabled, setPaymentsEnabled] = useState(false);
  
  useEffect(() => {
    // Check if the payments feature is enabled
    const checkFeatureFlag = () => {
      const isEnabled = featureFlagService.isFeatureEnabled(FEATURES.STRIPE_PAYMENTS);
      setPaymentsEnabled(isEnabled);
    };

    // Check initially
    checkFeatureFlag();

    // Set up interval to check periodically in case flag changes
    const interval = setInterval(checkFeatureFlag, 30000); // Check every 30 seconds

    return () => clearInterval(interval);
  }, []);
  
  const handleUpgrade = async () => {
    // Return early if payments are disabled
    if (!paymentsEnabled) {
      setError('Payment functionality is currently disabled.');
      return;
    }
    try {
      setError(''); // Clear previous errors
      setLoading(true);
      
      // Get the current URL for success and cancel redirects
      const baseUrl = window.location.origin;
      // Use absolute URLs for success and cancel (Stripe requires full URLs)
      const successUrl = `${baseUrl}/payment-success`;
      const cancelUrl = `${baseUrl}/payment-cancel`;
      
      console.log('Sending payment request with:', { 
        paymentType: 'NEGOTIATION', 
        successUrl, 
        cancelUrl 
      });
      
      // Call the payment service with required parameters
      const response = await paymentService.createCheckoutSession({
        paymentType: 'NEGOTIATION', // This must match the enum values in the backend
        successUrl,
        cancelUrl,
      });
      
      const stripe = await stripePromise;
      if (stripe && response && response.id) {
        // The API returns { id } not { sessionId }
        await stripe.redirectToCheckout({ sessionId: response.id });
      } else {
        console.error('Invalid response from payment service', response);
        setError('Unable to create checkout session. Please try again later.');
      }
    } catch (err) {
      console.error('Failed to start checkout', err);
      setError(`Payment error: ${err.message || 'Unable to process your payment request'}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container className="my-5 text-center">
      <h1>Upgrade to Premium</h1>
      <p className="mb-4">
        Unlock additional features and enhanced support by upgrading your
        account.
      </p>
      {error && <Alert variant="danger">{error}</Alert>}
      
      {!paymentsEnabled ? (
        <Card className="my-4 text-center">
          <Card.Body>
            <Card.Title>Coming Soon</Card.Title>
            <Card.Text>
              Premium upgrades are currently unavailable while we enhance our payment system.
              Please check back later or contact support for assistance.
            </Card.Text>
            <Button variant="secondary" href="/contact">
              Contact Support
            </Button>
          </Card.Body>
        </Card>
      ) : (
        <div className="d-grid gap-3 col-md-6 mx-auto">
        <Button 
          variant="success" 
          onClick={handleUpgrade} 
          data-testid="upgrade-btn"
          disabled={loading}
          size="lg"
        >
          {loading ? (
            <>
              <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" />
              <span className="ms-2">Processing...</span>
            </>
          ) : (
            'Upgrade Now'
          )}
        </Button>
        
        <div className="mt-3">
          <p>Already have a payment method?</p>
          <Button 
            variant="outline-secondary" 
            href="/billing-management"
          >
            Manage Billing Information
          </Button>
        </div>
      </div>
      )}
    </Container>
  );
};

export default PremiumUpgradePage;
